/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTraslados } from "../../../TrasladosContext";
import api from "../../../axiosConfig";
import { putConReintento, mensajeErrorAccion } from "../../../utils/reintentoRed";
import {
  Button,
  Typography,
  message,
  Spin,
  Card,
  Tag,
  Drawer,
} from "antd";
import {
  LeftOutlined,
  CheckCircleOutlined,
  CarOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  FlagOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  PhoneOutlined,
  AimOutlined,
  MenuOutlined,
  InfoCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import MapComponent from "../../../common/MapComponent";
import { calculateDistance } from "../../../utils/distance";
import OptionModal from "../../../common/OptionModal";
import FirmaPaciente from "../FirmaPaciente/FirmaPaciente";
import FallidoValidacionModal from "./FallidoValidacionModal";
import FinalizadoValidacionModal from "./FinalizadoValidacionModal";
import OtpModal from "../OTP/OtpModal";
import ObservacionesModal from "../../common/ObservacionesModal";
import { useOtp } from "../../../Contexts/OtpContext";
import gpsTrackingService from "../../../services/gpsTrackingService";
import useWakeLock from "../../../hooks/useWakeLock";
import useCurrentLocation from "../../../hooks/useCurrentLocation";
import { esAdministrador, rutaDetalleTraslado } from "../../../utils/perfil";

const { Text, Title } = Typography;

const TrasladoMapView = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const { traslados, updateTraslado } = useTraslados();
  const [traslado, setTraslado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoadingState] = useState(false);
  // Bloqueo síncrono contra dobles toques: el estado de React no cambia hasta el siguiente render,
  // y obtener la ubicación puede tardar hasta 10 s con el botón todavía activo
  const accionEnCurso = useRef(false);
  const setActionLoading = (valor) => {
    accionEnCurso.current = valor;
    setActionLoadingState(valor);
  };
  const tomarAccion = () => {
    if (accionEnCurso.current) return false;
    setActionLoading(true);
    return true;
  };
  const [showDetails, setShowDetails] = useState(false);

  // Administrador de vehículos: mismo detalle y mapa, sin acciones, sin GPS ni pantalla bloqueada
  const administrador = esAdministrador();

  // Verificar si tiene vehículo asignado (modo visualizador)
  const vehiculoPrincipal = localStorage.getItem("vehiculoPrincipal");
  const tieneVehiculo = vehiculoPrincipal && vehiculoPrincipal !== "null" && vehiculoPrincipal !== "undefined";

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Estados para modal de firma del paciente
  const [firmaModalVisible, setFirmaModalVisible] = useState(false);
  const [pendingFinalizarData, setPendingFinalizarData] = useState(null);

  // Estados para modal de Fallido por Validar
  const [fallidoModalVisible, setFallidoModalVisible] = useState(false);

  // Estados para modal de Finalizado por Validar (sin firma)
  const [finalizadoXvalidarModalVisible, setFinalizadoXvalidarModalVisible] = useState(false);

  // Estados para modal OTP
  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [otpTipo, setOtpTipo] = useState(null); // 'inicio' o 'finalizacion'
  const [pendingOtpAction, setPendingOtpAction] = useState(null);

  // Estado para modal de indicaciones del conductor
  const [indicacionesModalVisible, setIndicacionesModalVisible] = useState(false);

  // Hook de OTP
  const { requiresOtp } = useOtp();

  // Wake Lock para mantener la pantalla activa durante el traslado
  const { isSupported: wakeLockSupported, isLocked: screenLocked } = useWakeLock(!administrador);

  // Obtener ubicación actual del conductor en tiempo real
  const { location: currentLocation, error: locationError } = useCurrentLocation(!administrador, {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 5000,
  });

  const commonButtonStyle = {
    fontSize: 18,
    height: 60,
    borderRadius: 10,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const drawerButtonStyle = {
    ...commonButtonStyle,
    backgroundColor: "#ffffff",
    border: "1px solid #e8e8e8",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  };

  useEffect(() => {

    if (traslado && Number(traslado.id) === Number(id)) {
      setLoading(false);
      return;
    }

    const foundTraslado = traslados.find((t) => Number(t.id) === Number(id));

    if (foundTraslado) {
      setTraslado(foundTraslado);
      setLoading(false);
    } else {
      setLoading(true);
      const fetchTraslado = async () => {
        try {
          const response = await api.get(rutaDetalleTraslado(id));
          // El servidor responde { data: traslado }
          setTraslado(response.data?.data ?? response.data);
        } catch (error) {
          console.error("Error al obtener el traslado:", error);
          message.error("Error al obtener los detalles del traslado.");
        } finally {
          setLoading(false);
        }
      };
      fetchTraslado();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, traslados]);


  const handleBack = () => {
    navigate("/modules/dashboard");
  };

  const getStatusColor = (status) => {
    const statusColors = {
      Cancelado: "#f5222d",
      Finalizado: "#d9d9d9",
      Fallido: "#f5222d",
      Asignado: "#1890ff",
      "Pre-Asignado": "#40a9ff",
      Programado: "#722ed1",
      Enterado: "#52c41a",
      Iniciado: "#faad14",
      Aceptado: "#52c41a",
      "En Camino": "#faad14",
      Esperando: "#faad14",
      default: "#87d068",
    };
    return statusColors[status] || statusColors.default;
  };

  const handleAction = async (endpoint, successMessage) => {
    if (!tomarAccion()) return;
    try {
      const response = await api.put(endpoint);
      updateTraslado(response.data.traslado);
      setTraslado(response.data.traslado);
      message.success(successMessage);
    } catch (error) {
      console.error("Error al realizar la acción:", error);
      message.error(
        error.response?.data?.error || "Error al realizar la acción."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleAceptarTraslado = () => {
    handleAction(
      `/traslados/aceptar/${traslado.id}`,
      "Has aceptado el traslado."
    );
  };

  const handleEnCamino = () => {
    handleAction(
      `/traslados/en-camino/${traslado.id}`,
      "Has marcado el traslado como En Camino."
    );
  };

  const handleEsperando = () => {
    handleAction(
      `/traslados/esperando/${traslado.id}`,
      "Has marcado el traslado como Esperando."
    );
  };

  const handleIniciarTraslado = async (
    option = "recomendada",
    description = null
  ) => {
    if (accionEnCurso.current) return;
    // Verificar si requiere OTP
    if (requiresOtp(traslado, 'inicio')) {
      setPendingOtpAction({ option, description, type: 'inicio' });
      setOtpTipo('inicio');
      setOtpModalVisible(true);
      return;
    }
    if (!tomarAccion()) return;
    await handleInicioTraslado(option, description);
  };

  const handleFinalizarTraslado = async (
    option = "recomendada",
    description = null
  ) => {
    if (accionEnCurso.current) return;
    // Verificar si requiere OTP
    if (requiresOtp(traslado, 'finalizacion')) {
      setPendingOtpAction({ option, description, type: 'finalizacion' });
      setOtpTipo('finalizacion');
      setOtpModalVisible(true);
      return;
    }
    await handleFinTraslado(option, description);
  };

  const handleProblemTraslado = (action) => {
    setCurrentAction(action);
    setIsModalVisible(true);
  };

  const getModalTitle = () => {
    if (currentAction === "iniciar")
      return "Reportar un problema al iniciar el traslado";
    if (currentAction === "finalizar")
      return "Reportar un problema al finalizar el traslado";
    return "Acción";
  };

  const handleModalSubmit = async ({ option, description }) => {
    setIsModalVisible(false);
    if (!tomarAccion()) return;
    if (currentAction === "iniciar") {
      await handleInicioTraslado(option, description);
    } else if (currentAction === "finalizar") {
      await handleFinTraslado(option, description);
    }
    setCurrentAction(null);
  };

  /**
   * Lógica para Iniciar y Finalizar con validaciones de distancia
   */
  const handleInicioTraslado = async (option, description) => {
    if (option === "recomendada") {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const distance = calculateDistance(
            latitude,
            longitude,
            traslado.origen_coordenadas.coordinates[1],
            traslado.origen_coordenadas.coordinates[0]
          );
          if (distance <= 100) {
            await enviarIniciarTraslado(option, { latitude, longitude }, null);
          } else {
            message.warning(
              "Debes estar cerca del punto de recogida para iniciar el traslado."
            );
            setActionLoading(false);
          }
        },
        (error) => {
          console.error("Error al obtener la ubicación:", error);
          message.error("No se pudo obtener la ubicación actual.");
          setActionLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          await enviarIniciarTraslado(
            option,
            { latitude, longitude },
            description
          );
        },
        (error) => {
          console.error("Error al obtener la ubicación:", error);
          message.error("No se pudo obtener la ubicación actual.");
          setActionLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  };

  const handleFinTraslado = async (option, description) => {
    setActionLoading(true);
    if (option === "recomendada") {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const distance = calculateDistance(
            latitude,
            longitude,
            traslado.destino_coordenadas.coordinates[1],
            traslado.destino_coordenadas.coordinates[0]
          );
          if (distance <= 100) {
            // Guardar datos para después de la firma (INCLUYENDO COORDENADAS)
            setPendingFinalizarData({
              option,
              coordinates: { latitude, longitude },
              description: null
            });
            setActionLoading(false);
            // Mostrar modal de firma del paciente
            setFirmaModalVisible(true);
          } else {
            message.warning(
              "Debes estar cerca del punto de destino para finalizar el traslado."
            );
            setActionLoading(false);
          }
        },
        (error) => {
          console.error("Error al obtener la ubicación:", error);
          message.error("No se pudo obtener la ubicación actual.");
          setActionLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          // Guardar datos para después de la firma
          setPendingFinalizarData({
            option,
            coordinates: { latitude, longitude },
            description
          });
          setActionLoading(false);
          // Mostrar modal de firma del paciente
          setFirmaModalVisible(true);
        },
        (error) => {
          console.error("Error al obtener la ubicación:", error);
          message.error("No se pudo obtener la ubicación actual.");
          setActionLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  };

  /**
   * Peticiones a la API de inicio y fin
   */
  const enviarIniciarTraslado = async (option, coordinates, description) => {
    try {
      const payload = {
        option,
        coordinates: coordinates
          ? {
              type: "Point",
              coordinates: [coordinates.longitude, coordinates.latitude],
            }
          : null,
        description,
      };
      const response = await putConReintento(
        api,
        `/traslados/iniciar/${traslado.id}`,
        payload
      );
      updateTraslado(response.data.traslado);
      setTraslado(response.data.traslado);
      message.success("Traslado iniciado exitosamente.");

      // Iniciar seguimiento GPS
      console.log('Iniciando seguimiento GPS para traslado:', traslado.id);
      gpsTrackingService.iniciarSeguimiento(traslado.id, {
        tiempoEnvioSegundos: 30,
        distanciaMinimaMetros: 50,
        habilitarBatch: true,
      });
    } catch (error) {
      console.error("Error al iniciar el traslado:", error);
      message.error(mensajeErrorAccion(error, "Error al iniciar el traslado."));
    } finally {
      setActionLoading(false);
    }
  };

  const enviarFinalizarTraslado = async (option, coordinates, description, firmaPaciente = null) => {
    try {
      const payload = {
        option,
        coordinates: coordinates
          ? {
              type: "Point",
              coordinates: [coordinates.longitude, coordinates.latitude],
            }
          : null,
        description,
        firma_paciente: firmaPaciente,
      };

      console.log("=== PAYLOAD COMPLETO ===");
      console.log("Option:", option);
      console.log("Coordinates:", coordinates);
      console.log("Description:", description);
      console.log("Firma Paciente:", firmaPaciente);
      console.log("Payload final:", JSON.stringify(payload, null, 2));

      const response = await putConReintento(
        api,
        `/traslados/finalizar/${traslado.id}`,
        payload
      );
      updateTraslado(response.data.traslado);
      setTraslado(response.data.traslado);
      message.success("Traslado finalizado exitosamente.");

      // Detener seguimiento GPS
      console.log('Deteniendo seguimiento GPS para traslado:', traslado.id);
      gpsTrackingService.detenerSeguimiento();
    } catch (error) {
      console.error("Error al finalizar el traslado:", error);
      console.error("Error response data:", error.response?.data);
      console.error("Error status:", error.response?.status);
      message.error(mensajeErrorAccion(error, "Error al finalizar el traslado."));
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handler para cuando se envía la firma del paciente
   */
  const handleFirmaPacienteSubmit = async (firmaData) => {
    setFirmaModalVisible(false);
    setActionLoading(true);

    if (pendingFinalizarData) {
      await enviarFinalizarTraslado(
        pendingFinalizarData.option,
        pendingFinalizarData.coordinates,
        pendingFinalizarData.description,
        firmaData
      );
      setPendingFinalizarData(null);
    }
  };

  /**
   * Handler para cancelar el modal de firma
   */
  const handleFirmaModalCancel = () => {
    setFirmaModalVisible(false);
    setPendingFinalizarData(null);
    setActionLoading(false);
  };

  /**
   * Handler para cuando el OTP es verificado exitosamente
   */
  const handleOtpVerified = async (verified) => {
    setOtpModalVisible(false);

    if (verified && pendingOtpAction) {
      const { option, description, type } = pendingOtpAction;

      if (type === 'inicio') {
        if (!tomarAccion()) return;
        await handleInicioTraslado(option, description);
      } else if (type === 'finalizacion') {
        await handleFinTraslado(option, description);
      }
    }

    setPendingOtpAction(null);
    setOtpTipo(null);
  };

  /**
   * Handler para cancelar el modal OTP
   */
  const handleOtpCancel = () => {
    setOtpModalVisible(false);
    setPendingOtpAction(null);
    setOtpTipo(null);
    setActionLoading(false);
  };

  /**
   * Handler para abrir modal de Fallido por Validar
   */
  const handleMarcarFallido = () => {
    setFallidoModalVisible(true);
  };

  /**
   * Ubicación actual para cerrar el traslado (fallido / sin firma): se pide una lectura nueva del GPS
   * porque ese punto queda como fin de la ruta. Si el GPS no responde se usa la última del seguimiento.
   */
  const MENSAJE_SIN_UBICACION = "No se pudo obtener su ubicación actual. Active el GPS e intente de nuevo.";
  const obtenerUbicacionActual = () =>
    new Promise((resolve) => {
      const respaldo = () =>
        resolve(
          currentLocation?.latitude != null && currentLocation?.longitude != null
            ? { latitude: currentLocation.latitude, longitude: currentLocation.longitude }
            : null
        );
      if (!navigator.geolocation) {
        respaldo();
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
        respaldo,
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
      );
    });

  /**
   * Handler para confirmar el fallido (después de 15 min)
   */
  const handleConfirmarFallido = async ({ motivo, ubicacionInicial, tiempoEspera }) => {
    const ubicacion = await obtenerUbicacionActual();
    if (!ubicacion) {
      message.error(MENSAJE_SIN_UBICACION);
      throw new Error(MENSAJE_SIN_UBICACION);
    }
    try {
      const payload = {
        coordinates: {
          type: "Point",
          coordinates: [ubicacion.longitude, ubicacion.latitude],
        },
        motivo,
        tiempo_espera: tiempoEspera,
        ubicacion_inicial: ubicacionInicial,
      };

      const response = await putConReintento(api, `/traslados/fallido-x-validar/${traslado.id}`, payload);

      updateTraslado(response.data.traslado);
      setTraslado(response.data.traslado);
      message.success("Traslado marcado como Fallido por validar exitosamente");
      setFallidoModalVisible(false);

      // Detener seguimiento GPS
      console.log('Deteniendo seguimiento GPS para traslado fallido:', traslado.id);
      gpsTrackingService.detenerSeguimiento();
    } catch (error) {
      console.error("Error al marcar traslado como fallido:", error);
      message.error(mensajeErrorAccion(error, "Error al marcar traslado como fallido"));
      throw error; // Re-lanzar para que el modal maneje el estado de loading
    }
  };

  /**
   * Handler para abrir modal de Finalizado por Validar (sin firma)
   */
  const handleFinalizarSinFirma = () => {
    setFinalizadoXvalidarModalVisible(true);
  };

  /**
   * Handler para confirmar el finalizado por validar (bajo juramento)
   */
  const handleConfirmarFinalizadoXvalidar = async ({ motivo }) => {
    const ubicacion = await obtenerUbicacionActual();
    if (!ubicacion) {
      message.error(MENSAJE_SIN_UBICACION);
      throw new Error(MENSAJE_SIN_UBICACION);
    }
    try {
      const payload = {
        coordinates: {
          type: "Point",
          coordinates: [ubicacion.longitude, ubicacion.latitude],
        },
        motivo,
        juramento: true,
      };

      // Con reintento: si se perdió la respuesta el servidor responde éxito (ya_marcado) sin duplicar
      const response = await putConReintento(api, `/traslados/finalizado-x-validar/${traslado.id}`, payload);

      updateTraslado(response.data.traslado);
      setTraslado(response.data.traslado);
      message.success("Traslado marcado como Finalizado por validar. Pendiente de validación OTP en Centro de Control.");
      setFinalizadoXvalidarModalVisible(false);

      // Detener seguimiento GPS
      console.log('Deteniendo seguimiento GPS para traslado finalizado por validar:', traslado.id);
      gpsTrackingService.detenerSeguimiento();
    } catch (error) {
      console.error("Error al marcar traslado como finalizado por validar:", error);
      message.error(mensajeErrorAccion(error, "Error al marcar traslado como finalizado por validar"));
      throw error;
    }
  };

  /**
   * Render de botones de acción según el estado
   */
  const renderActionButtons = () => {
    if (administrador) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '15px',
          backgroundColor: '#e6f4ff',
          borderRadius: 10,
          border: '1px solid #91caff'
        }}>
          <EyeOutlined style={{ fontSize: 32, color: '#1677ff', marginBottom: 8 }} />
          <Text style={{ fontSize: 16, color: '#0958d9', textAlign: 'center', fontWeight: 'bold' }}>
            Solo lectura - Administrador de vehículos
          </Text>
          {(traslado.vehiculo || traslado.conductor) && (
            <Text style={{ fontSize: 14, color: '#0958d9', textAlign: 'center', marginTop: 4 }}>
              <CarOutlined style={{ marginRight: 6 }} />
              {[traslado.vehiculo?.placa, traslado.conductor?.nombre].filter(Boolean).join(" - ")}
            </Text>
          )}
        </div>
      );
    }

    // Si no tiene vehículo asignado, mostrar modo visualizador
    if (!tieneVehiculo) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '15px',
          backgroundColor: '#fff7e6',
          borderRadius: 10,
          border: '1px solid #ffd591'
        }}>
          <EyeOutlined style={{ fontSize: 32, color: '#fa8c16', marginBottom: 8 }} />
          <Text style={{ fontSize: 16, color: '#ad6800', textAlign: 'center', fontWeight: 'bold' }}>
            Modo Visualizador
          </Text>
          <Text style={{ fontSize: 14, color: '#ad6800', textAlign: 'center', marginTop: 4 }}>
            Sin vehículo asignado - Solo lectura
          </Text>
        </div>
      );
    }

    switch (traslado.status) {
      case "Enterado":
        return (
          <Button
            type="primary"
            icon={<CheckCircleOutlined style={{ fontSize: 22 }} />}
            onClick={handleAceptarTraslado}
            style={{
              width: "100%",
              height: 60,
              fontSize: 18,
              borderRadius: 10,
            }}
            loading={actionLoading}
            aria-label="Aceptar Traslado"
          >
            Aceptar Traslado
          </Button>
        );
      case "Aceptado":
        return (
          <Button
            type="primary"
            icon={<CarOutlined style={{ fontSize: 22 }} />}
            onClick={handleEnCamino}
            style={{
              width: "100%",
              height: 60,
              fontSize: 18,
              borderRadius: 10,
            }}
            loading={actionLoading}
            aria-label="En Camino"
          >
            En Camino
          </Button>
        );
      case "En Camino":
        return (
          <Button
            type="primary"
            icon={<PauseOutlined style={{ fontSize: 22 }} />}
            onClick={handleEsperando}
            style={{
              width: "100%",
              height: 60,
              fontSize: 18,
              borderRadius: 10,
            }}
            loading={actionLoading}
            aria-label="Esperando"
          >
            Esperando
          </Button>
        );
      case "Esperando":
        return (
          <>
            <Button
              type="primary"
              icon={<PlayCircleOutlined style={{ fontSize: 22 }} />}
              onClick={() => handleIniciarTraslado()}
              disabled={false}
              style={{
                width: "100%",
                height: 60,
                fontSize: 18,
                marginBottom: 10,
                borderRadius: 10,
              }}
              loading={actionLoading}
              aria-label="Iniciar Traslado"
              title={
                ""
              }
            >
              Iniciar
            </Button>
            <Button
              danger
              icon={<WarningOutlined style={{ fontSize: 22 }} />}
              onClick={handleMarcarFallido}
              style={{
                width: "100%",
                height: 60,
                fontSize: 18,
                marginBottom: 10,
                borderRadius: 10,
                backgroundColor: "#fff",
                borderColor: "#ff4d4f",
                color: "#ff4d4f",
              }}
              disabled={actionLoading}
              aria-label="Marcar como Fallido"
              title="Marcar como Fallido (requiere 15 min de espera)"
            >
              Marcar como Fallido
            </Button>
            <Button
              icon={<CheckCircleOutlined style={{ fontSize: 22 }} />}
              onClick={handleFinalizarSinFirma}
              style={{
                width: "100%",
                height: 60,
                fontSize: 18,
                marginBottom: 10,
                borderRadius: 10,
                backgroundColor: "#fff",
                borderColor: "#722ED1",
                color: "#722ED1",
              }}
              disabled={actionLoading}
              aria-label="Finalizar sin firma"
              title="Finalizar sin firma (requiere validación OTP)"
            >
              Finalizar sin firma
            </Button>
            <Button
              type="default"
              icon={<ExclamationCircleOutlined style={{ fontSize: 22 }} />}
              onClick={() => handleProblemTraslado("iniciar")}
              style={{
                width: "100%",
                height: 60,
                fontSize: 18,
                borderRadius: 10,
              }}
              disabled={false}
              aria-label="Tengo un problema al iniciar"
              title=""
            >
              Tengo un problema
            </Button>
          </>
        );
      case "Iniciado":
        return (
          <>
            <Button
              type="primary"
              icon={<FlagOutlined style={{ fontSize: 22 }} />}
              onClick={() => handleFinalizarTraslado()}
              disabled={false}
              style={{
                width: "100%",
                height: 60,
                fontSize: 18,
                marginBottom: 10,
                borderRadius: 10,
              }}
              loading={actionLoading}
              aria-label="Finalizar Traslado"
              title=""
            >
              Finalizar
            </Button>
            <Button
              danger
              icon={<WarningOutlined style={{ fontSize: 22 }} />}
              onClick={handleMarcarFallido}
              style={{
                width: "100%",
                height: 60,
                fontSize: 18,
                marginBottom: 10,
                borderRadius: 10,
                backgroundColor: "#fff",
                borderColor: "#ff4d4f",
                color: "#ff4d4f",
              }}
              disabled={actionLoading}
              aria-label="Marcar como Fallido"
              title="Marcar como Fallido (requiere 15 min de espera)"
            >
              Marcar como Fallido
            </Button>
            <Button
              icon={<CheckCircleOutlined style={{ fontSize: 22 }} />}
              onClick={handleFinalizarSinFirma}
              style={{
                width: "100%",
                height: 60,
                fontSize: 18,
                marginBottom: 10,
                borderRadius: 10,
                backgroundColor: "#fff",
                borderColor: "#722ED1",
                color: "#722ED1",
              }}
              disabled={actionLoading}
              aria-label="Finalizar sin firma"
              title="Finalizar sin firma (requiere validación OTP)"
            >
              Finalizar sin firma
            </Button>
            <Button
              type="default"
              icon={<ExclamationCircleOutlined style={{ fontSize: 22 }} />}
              onClick={() => handleProblemTraslado("finalizar")}
              style={{
                width: "100%",
                height: 60,
                fontSize: 18,
                borderRadius: 10,
              }}
              disabled={false}
              aria-label="Tengo un problema al finalizar"
              title=""
            >
              Tengo un problema
            </Button>
          </>
        );
      case "Finalizado":
        return (
          <Text type="success" style={{ fontSize: 22 }}>
            Traslado Finalizado
          </Text>
        );
      default:
        return null;
    }
  };

  const origin = useMemo(() => {
    const coords = traslado?.origen_coordenadas?.coordinates;
    // Coordinates are stored in GeoJSON format [longitude, latitude]
    return coords ? { latitude: coords[1], longitude: coords[0] } : null;
  }, [traslado]);

  const destination = useMemo(() => {
    const coords = traslado?.destino_coordenadas?.coordinates;
    // Coordinates are stored in GeoJSON format [longitude, latitude]
    return coords ? { latitude: coords[1], longitude: coords[0] } : null;
  }, [traslado]);

  if (loading || !traslado) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 20,
          backgroundColor: "#f7f9fc",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  /**
   * Render principal
   */
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "linear-gradient(135deg, #e8f0fe, #ffffff)",
      }}
    >
      {/* Indicador de pantalla bloqueada */}
      {wakeLockSupported && screenLocked && (
        <div
          style={{
            backgroundColor: '#52c41a',
            color: '#fff',
            padding: '4px 12px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            flexShrink: 0,
          }}
        >
          <span>🔒</span>
          <span>Pantalla bloqueada - No se apagará</span>
        </div>
      )}

      {/* Indicador de ubicación GPS */}
      {locationError ? (
        <div
          style={{
            backgroundColor: '#ff4d4f',
            color: '#fff',
            padding: '4px 12px',
            textAlign: 'center',
            fontSize: '11px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            flexShrink: 0,
          }}
        >
          <span>⚠️</span>
          <span>Error de ubicación: {locationError}</span>
        </div>
      ) : currentLocation && (
        <div
          style={{
            backgroundColor: '#1890ff',
            color: '#fff',
            padding: '4px 12px',
            textAlign: 'center',
            fontSize: '11px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            flexShrink: 0,
          }}
        >
          <span>📍</span>
          <span>GPS Activo - Precisión: {currentLocation.accuracy?.toFixed(0)}m</span>
        </div>
      )}

      {/* Encabezado (Volver + Menú Lateral) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "20px",
          gap: 10,
          flexShrink: 0,
          backgroundColor: "#ffffff",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ flex: "0 0 auto", width: 120 }}>
          <Button
            icon={<LeftOutlined style={{ fontSize: 22 }} />}
            onClick={handleBack}
            style={{
              width: "100%",
              height: 60,
              fontSize: 18,
              borderRadius: 10,
              backgroundColor: "#1890ff",
              color: "#fff",
              border: "none",
            }}
            aria-label="Volver a la Lista"
          >
            Volver
          </Button>
        </div>
        {!administrador && (
        <div style={{ flex: 1 }}>
          <Button
            icon={<MenuOutlined style={{ fontSize: 22 }} />}
            onClick={() => setDrawerVisible(true)}
            style={{
              width: "100%",
              height: 60,
              fontSize: 18,
              borderRadius: 10,
              backgroundColor: "#40a9ff",
              color: "#fff",
              border: "none",
            }}
            aria-label="Acciones"
          >
            Acciones
          </Button>
        </div>
        )}
      </div>

      {/* Menú lateral (Drawer) */}
      <Drawer
        title="Acciones"
        placement="left"
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        bodyStyle={{ backgroundColor: "#f0f2f5", padding: 20 }}
        headerStyle={{
          backgroundColor: "#1890ff",
          color: "#fff",
          fontSize: 20,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
          <Button
            icon={<PhoneOutlined style={{ fontSize: 24 }} />}
            onClick={() => {
              if (traslado.numero_contacto_paciente) {
                window.open(
                  `tel:${traslado.numero_contacto_paciente}`,
                  "_self"
                );
              } else {
                message.warning(
                  "No se ha proporcionado el número de contacto del paciente."
                );
              }
              setDrawerVisible(false);
            }}
            style={drawerButtonStyle}
          >
            Llamar al Paciente
          </Button>

          <Button
            icon={<PhoneOutlined style={{ fontSize: 24 }} />}
            onClick={() => {
              if (traslado.numero_contacto_responsable) {
                window.open(
                  `tel:${traslado.numero_contacto_responsable}`,
                  "_self"
                );
              } else {
                message.warning(
                  "No se ha proporcionado el número de contacto del responsable."
                );
              }
              setDrawerVisible(false);
            }}
            style={drawerButtonStyle}
          >
            Llamar al Responsable
          </Button>

          {/* Botones para abrir Google Maps con direcciones */}
          {traslado.origen_coordenadas &&
            traslado.origen_coordenadas.coordinates && (
              <Button
                icon={<AimOutlined style={{ fontSize: 24 }} />}
                onClick={() => {
                  window.open(
                    `https://www.google.com/maps/dir/?api=1&destination=${traslado.origen_coordenadas.coordinates[1]},${traslado.origen_coordenadas.coordinates[0]}`,
                    "_blank"
                  );
                  setDrawerVisible(false);
                }}
                style={drawerButtonStyle}
              >
                Ir a Dirección de Origen
              </Button>
            )}
          {traslado.destino_coordenadas &&
            traslado.destino_coordenadas.coordinates && (
              <Button
                icon={<AimOutlined style={{ fontSize: 24 }} />}
                onClick={() => {
                  window.open(
                    `https://www.google.com/maps/dir/?api=1&destination=${traslado.destino_coordenadas.coordinates[1]},${traslado.destino_coordenadas.coordinates[0]}`,
                    "_blank"
                  );
                  setDrawerVisible(false);
                }}
                style={drawerButtonStyle}
              >
                Ir a Dirección de Destino
              </Button>
            )}
        </div>
      </Drawer>

      {/* Información del traslado */}
      <div style={{ padding: "0 20px 10px 20px", flexShrink: 0 }}>
        <Card
          bordered={false}
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            borderTop: "4px solid #1890ff",
          }}
        >
          <Title level={4} style={{ marginBottom: 0 }}>
            <Tag color={getStatusColor(traslado.status)}>{traslado.status}</Tag>
            {traslado.inicio_status && (
              <Tag
                color={getStatusColor(traslado.inicio_status)}
                style={{ marginLeft: 8 }}
              >
                {traslado.inicio_status}
              </Tag>
            )}
            {traslado.fin_status && (
              <Tag
                color={getStatusColor(traslado.fin_status)}
                style={{ marginLeft: 8 }}
              >
                {traslado.fin_status}
              </Tag>
            )}
          </Title>

          <div style={{ marginTop: 10 }}>
            <Text style={{ fontSize: 18 }}>
              <EnvironmentOutlined style={{ fontSize: 22, marginRight: 8 }} />
              {traslado.origen_lugar} - {traslado.origen_direccion}
              {traslado.origen_barrio ? ` - ${traslado.origen_barrio}` : ""} -{" "}
              {traslado.origen_ciudad}
            </Text>
            {showDetails && (
              <>
                <br />
                <Text style={{ fontSize: 18 }}>
                  <EnvironmentOutlined
                    style={{ fontSize: 22, marginRight: 8 }}
                  />
                  {traslado.destino_lugar} - {traslado.destino_direccion}
                  {traslado.destino_barrio ? ` - ${traslado.destino_barrio}` : ""} -{" "}
                  {traslado.destino_ciudad}
                </Text>
                <br />
                <Text style={{ fontSize: 18 }}>
                  <ClockCircleOutlined
                    style={{ fontSize: 22, marginRight: 8 }}
                  />
                  Hora Recogida: {traslado.hora_recogida.slice(0, 5)}
                </Text>
                <br />
                <Text style={{ fontSize: 18 }}>
                  <Text strong>{traslado.nombre_paciente}</Text>
                </Text>
                {traslado.indicaciones_conductor && (
                  <>
                    <br />
                    <div
                      style={{
                        marginTop: 8,
                        padding: 10,
                        backgroundColor: "#e6f7ff",
                        borderRadius: 8,
                        border: "1px solid #91d5ff",
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 14,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        <InfoCircleOutlined
                          style={{ marginRight: 6, color: "#1890ff" }}
                        />
                        {traslado.indicaciones_conductor}
                      </Text>
                      {traslado.indicaciones_conductor.length > 80 && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setIndicacionesModalVisible(true)}
                          style={{ padding: 0, marginTop: 4 }}
                        >
                          Ver todo
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div style={{ marginTop: 10, textAlign: "right" }}>
            <Button
              type="link"
              onClick={() => setShowDetails(!showDetails)}
              style={{ fontSize: 16 }}
            >
              {showDetails ? "Ocultar" : "Ver más"}
            </Button>
          </div>
        </Card>
      </div>

      {/* Mapa */}
      <div style={{ flex: 1, position: "relative" }}>
        <MapComponent
          origin={origin}
          destination={destination}
          driverLocation={currentLocation}
        />
      </div>

      {/* Botones de acción */}
      <div
        style={{
          padding: 20,
          backgroundColor: "#ffffff",
          flexShrink: 0,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {renderActionButtons()}
      </div>

      {/* Modal para opciones (problemas, etc.) */}
      {isModalVisible && currentAction && (
        <OptionModal
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            setCurrentAction(null);
          }}
          onSubmit={handleModalSubmit}
          title={getModalTitle()}
          action="problema"
          parentAction={currentAction}
        />
      )}

      {/* Modal para captura de firma del paciente */}
      <FirmaPaciente
        visible={firmaModalVisible}
        onCancel={handleFirmaModalCancel}
        onSubmit={handleFirmaPacienteSubmit}
        loading={actionLoading}
        trasladoData={traslado}
      />

      {/* Modal para marcar traslado como Fallido por Validar */}
      <FallidoValidacionModal
        visible={fallidoModalVisible}
        onClose={() => setFallidoModalVisible(false)}
        onConfirm={handleConfirmarFallido}
        currentLocation={currentLocation}
      />

      {/* Modal para marcar traslado como Finalizado por Validar (sin firma) */}
      <FinalizadoValidacionModal
        visible={finalizadoXvalidarModalVisible}
        onClose={() => setFinalizadoXvalidarModalVisible(false)}
        onConfirm={handleConfirmarFinalizadoXvalidar}
      />

      {/* Modal para ver indicaciones del conductor completas */}
      {traslado?.indicaciones_conductor && (
        <ObservacionesModal
          visible={indicacionesModalVisible}
          onClose={() => setIndicacionesModalVisible(false)}
          titulo="Indicaciones del Conductor"
          contenido={traslado.indicaciones_conductor}
          icono={InfoCircleOutlined}
          iconColor="#1890ff"
        />
      )}

      {/* Modal para verificación OTP */}
      {traslado && (
        <OtpModal
          visible={otpModalVisible}
          onCancel={handleOtpCancel}
          onVerified={handleOtpVerified}
          trasladoId={traslado.id}
          tipo={otpTipo || 'inicio'}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default TrasladoMapView;
